import config from "../config/config";
import { USER_LOCALSTORAGE_KEY } from "../config/profile";

const {
  URL: {
    STOP_APP_URL_BACK,
    STOP_APP_URL_AFIP,
    STOP_APP_URL_BH,
    // STOP_APP_BASENAME,
    STOP_APP_URL,
  },
} = config;

const MESSAGE_ERROR = {
  estado: 0,
  message: "No se pudo realizar la petición",
};

const pendingRequests = {};
let refreshPromise = null;

const REFRESH_URL = "/login/refresh";
const LOGIN_URL = "/login";

/**
 * Configuración centralizada de APIs.
 *
 * IMPORTANTE:
 * - baseUrl debe venir desde el config.js generado por variables de entorno.
 * - Si una API tiene versión, conviene que venga incluida en la variable.
 *
 * Ejemplo:
 *   STOP_APP_URL_BACK=/backend
 *   STOP_APP_URL_AFIP=/afip-task
 *   STOP_APP_URL_BH=/bh/v1.0.0
 *
 * De esta forma los services consumen:
 *   connection({ api: "bh", url: "/plantas" })
 *
 * Y connection resuelve:
 *   /bh/v1.0.0/plantas
 */
const API_CONFIG = {
  backend: {
    baseUrl: STOP_APP_URL_BACK,
    aliases: [STOP_APP_URL_BACK, "/backend"],
  },
  afip: {
    baseUrl: STOP_APP_URL_AFIP,
    aliases: [STOP_APP_URL_AFIP, "/afip-task", "/afip"],
  },
  bh: {
    baseUrl: STOP_APP_URL_BH,
    aliases: [STOP_APP_URL_BH, "/bh"],
  },
};

async function parseResponse(fetchResponse, responseType) {
  if (responseType === "json") {
    return await fetchResponse.json().catch(() => ({}));
  }

  if (responseType === "blob") {
    return await fetchResponse.blob();
  }

  if (responseType === "text") {
    return await fetchResponse.text();
  }

  throw new Error(`responseType '${responseType}' no es soportado.`);
}

function buildHeaders({ formDataMode = false, extraHeaders = {} }) {
  const headers = new Headers({
    "Accept-Charset": "utf-8",
  });

  if (!formDataMode) {
    headers.set("Content-Type", "application/json");
  }

  Object.entries(extraHeaders || {}).forEach(([key, value]) => {
    headers.set(key, value);
  });

  return headers;
}

/**
 * Detecta URLs absolutas HTTP/HTTPS y protocol-relative.
 *
 * Ejemplos válidos:
 * - https://www.google.com/captcha
 * - http://localhost:3000/api
 * - //cdn.example.com/resource.js
 *
 * Estas URLs no deben recibir prefijo de API.
 */
function isAbsoluteHttpUrl(url = "") {
  return /^https?:\/\//i.test(String(url)) || /^\/\//.test(String(url));
}

/**
 * Normaliza un path relativo agregando "/" inicial.
 *
 * No modifica URLs absolutas.
 */
function normalizePath(path = "") {
  if (!path) return "";

  const value = String(path);

  if (isAbsoluteHttpUrl(value)) {
    return value;
  }

  return value.startsWith("/") ? value : `/${value}`;
}

function trimRightSlash(value = "") {
  return String(value || "").replace(/\/+$/, "");
}

function trimLeftSlash(value = "") {
  return String(value || "").replace(/^\/+/, "");
}

/**
 * Une baseUrl + path sin duplicar ni perder slashes.
 *
 * Respeta query params y hash porque no parte la URL por "?" ni "#".
 *
 * Ejemplo:
 *   joinUrl("/bh/v1.0.0", "/plantas?id=1")
 *   => "/bh/v1.0.0/plantas?id=1"
 */
function joinUrl(baseUrl = "", path = "") {
  const cleanBase = trimRightSlash(baseUrl);
  const cleanPath = trimLeftSlash(path);

  if (!cleanBase) return cleanPath ? `/${cleanPath}` : "";
  if (!cleanPath) return cleanBase;

  return `${cleanBase}/${cleanPath}`;
}

/**
 * Devuelve true si el path empieza con un alias/base conocido.
 *
 * Soporta:
 * - /bh
 * - /bh/
 * - /bh?
 * - /bh/v1.0.0
 * - /backend/...
 */
function pathStartsWithAlias(path = "", alias = "") {
  if (!alias) return false;

  const cleanPath = normalizePath(path);
  const cleanAlias = normalizePath(alias);

  if (!cleanAlias || cleanAlias === "/") return false;

  return (
    cleanPath === cleanAlias ||
    cleanPath.startsWith(`${cleanAlias}/`) ||
    cleanPath.startsWith(`${cleanAlias}?`) ||
    cleanPath.startsWith(`${cleanAlias}#`)
  );
}

/**
 * Detecta la API a partir del prefijo recibido en la URL.
 *
 * Ejemplos:
 *   /bh/plantas              => bh
 *   /bh/v1.0.0/plantas       => bh
 *   /backend/usuarios        => backend
 *   /afip-task/consulta      => afip
 */
function findApiByUrl(url = "") {
  const normalizedUrl = normalizePath(url);

  return (
    Object.entries(API_CONFIG).find(([, apiConfig]) => {
      return (apiConfig.aliases || []).some((alias) =>
        pathStartsWithAlias(normalizedUrl, alias)
      );
    })?.[0] || null
  );
}

/**
 * Resuelve el nombre de API a partir del modelo nuevo o los flags legacy.
 *
 * Prioridad:
 * 1. api explícita: connection({ api: "bh" })
 * 2. apiAfip legacy
 * 3. useStopAppBase legacy
 * 4. backend default
 */
function resolveApiName({ api, apiAfip, useStopAppBase }) {
  if (api) return api;

  // Compatibilidad con llamadas existentes.
  if (apiAfip) return "afip";
  if (useStopAppBase) return "bh";

  return "backend";
}

/**
 * Quita un alias/base del comienzo de la URL si está presente.
 *
 * Sirve para transformar:
 *   url=/bh/plantas
 *   baseUrl=/bh/v1.0.0
 *
 * en:
 *   /plantas
 *
 * para luego unir:
 *   /bh/v1.0.0 + /plantas
 *
 * Resultado:
 *   /bh/v1.0.0/plantas
 */
function stripAliasFromUrl(url = "", alias = "") {
  const cleanUrl = normalizePath(url);
  const cleanAlias = normalizePath(alias);

  if (!cleanAlias || cleanAlias === "/") return cleanUrl;

  if (cleanUrl === cleanAlias) return "/";

  if (
    cleanUrl.startsWith(`${cleanAlias}/`) ||
    cleanUrl.startsWith(`${cleanAlias}?`) ||
    cleanUrl.startsWith(`${cleanAlias}#`)
  ) {
    return cleanUrl.slice(cleanAlias.length) || "/";
  }

  return cleanUrl;
}

/**
 * Quita de la URL cualquiera de los aliases configurados para una API.
 *
 * Se ordenan por longitud descendente para que primero matchee la base más específica.
 *
 * Ejemplo:
 *   aliases: ["/bh/v1.0.0", "/bh"]
 *
 * Si url=/bh/v1.0.0/plantas, primero quita /bh/v1.0.0.
 * Si url=/bh/plantas, quita /bh.
 */
function stripKnownApiAliasFromUrl(url = "", apiConfig = {}) {
  const aliases = [...(apiConfig.aliases || []), apiConfig.baseUrl]
    .filter(Boolean)
    .sort((a, b) => String(b).length - String(a).length);

  let cleanUrl = normalizePath(url);

  for (const alias of aliases) {
    if (pathStartsWithAlias(cleanUrl, alias)) {
      cleanUrl = stripAliasFromUrl(cleanUrl, alias);
      break;
    }
  }

  return cleanUrl;
}

/**
 * Valida conflictos entre API explícita y prefijo detectado.
 *
 * Ejemplo inválido:
 *   connection({ api: "bh", url: "/backend/usuarios" })
 *
 * En ese caso es mejor fallar explícitamente en vez de pegarle a una URL inesperada.
 */
function validateApiConflict({ explicitApiName, detectedApiName, url }) {
  if (!explicitApiName || !detectedApiName) return;

  if (explicitApiName !== detectedApiName) {
    throw new Error(
      `La API explícita '${explicitApiName}' no coincide con el prefijo detectado en la URL '${url}' (${detectedApiName}).`
    );
  }
}

/**
 * Resolución centralizada de URL.
 *
 * Casos soportados:
 *
 * 1. API explícita:
 *    connection({ api: "bh", url: "/plantas" })
 *    => STOP_APP_URL_BH + /plantas
 *
 * 2. Flags legacy:
 *    connection({ useStopAppBase: true, url: "/plantas" })
 *    => STOP_APP_URL_BH + /plantas
 *
 * 3. URL con prefijo conocido:
 *    connection({ url: "/bh/plantas" })
 *    => detecta "bh", quita "/bh" y agrega STOP_APP_URL_BH
 *
 * 4. URL con base completa:
 *    connection({ url: "/bh/v1.0.0/plantas" })
 *    => detecta "bh" y evita duplicar prefijos
 *
 * 5. URL absoluta externa o interna:
 *    connection({ url: "https://www.google.com/captcha" })
 *    => se respeta tal cual
 *
 * 6. absoluteUrl:
 *    connection({ absoluteUrl: true, url: "/custom/path" })
 *    => se respeta tal cual
 *
 * 7. Sin API ni prefijo conocido:
 *    connection({ url: "/estado_terminales/getAllTerminales" })
 *    => backend default
 */
function resolveFinalUrl({
  api,
  apiAfip,
  useStopAppBase,
  url,
  absoluteUrl,
}) {
  if (absoluteUrl || isAbsoluteHttpUrl(url)) {
    return url;
  }

  const normalizedUrl = normalizePath(url);

  const explicitApiName = api || null;
  const detectedApiName = findApiByUrl(normalizedUrl);

  validateApiConflict({
    explicitApiName,
    detectedApiName,
    url: normalizedUrl,
  });

  const apiName =
    explicitApiName ||
    detectedApiName ||
    resolveApiName({
      api,
      apiAfip,
      useStopAppBase,
    });

  const apiConfig = API_CONFIG[apiName];

  if (!apiConfig) {
    throw new Error(
      `API '${apiName}' no es válida. APIs disponibles: ${Object.keys(API_CONFIG).join(", ")}`
    );
  }

  if (!apiConfig.baseUrl) {
    throw new Error(`API '${apiName}' no tiene baseUrl configurada.`);
  }

  const cleanUrl = stripKnownApiAliasFromUrl(normalizedUrl, apiConfig);

  return joinUrl(apiConfig.baseUrl, cleanUrl);
}

/**
 * El refresh siempre se realiza contra el backend principal.
 *
 * Se mantiene igual que antes:
 *   STOP_APP_URL_BACK + /login/refresh
 */
async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const response = await fetch(`${STOP_APP_URL_BACK}${REFRESH_URL}`, {
      method: "POST",
      headers: buildHeaders({
        formDataMode: false,
        extraHeaders: {},
      }),
      credentials: "include",
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.message || "No se pudo refrescar la sesión");
    }

    return true;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export default async function connection({
  method = "GET",
  url = "",
  extraHeaders = {},
  body = {},
  formDataMode = false,
  signal = null,

  // Nuevo nombre recomendado.
  abortRepeat = false,

  // Legacy typo: se mantiene para no romper llamadas actuales.
  abortRepect = false,

  responseType = "json",

  // Nuevo modelo recomendado.
  // Ejemplo: api: "backend" | "afip" | "bh"
  api = null,

  // Flags legacy.
  apiAfip = false,
  useStopAppBase = false,

  // Fuerza a usar la URL tal cual, sin resolución de API.
  absoluteUrl = false,
}) {
  let finalUrl = "";

  try {
    finalUrl = resolveFinalUrl({
      api,
      apiAfip,
      useStopAppBase,
      url,
      absoluteUrl,
    });

    const shouldAbortRepeated = abortRepeat || abortRepect;

    if (shouldAbortRepeated && pendingRequests[finalUrl]) {
      pendingRequests[finalUrl].abort();
    }

    const controller = new AbortController();
    const finalSignal = signal || controller.signal;

    pendingRequests[finalUrl] = controller;

    const makeOptions = () => {
      const options = {
        method,
        headers: buildHeaders({
          formDataMode,
          extraHeaders,
        }),
        redirect: "follow",
        signal: finalSignal,
        credentials: "include",
      };

      if (body && ["POST", "PUT", "PATCH"].includes(method)) {
        options.body = formDataMode ? body : JSON.stringify(body);
      }

      return options;
    };

    const isRefreshRequest =
      finalUrl.includes(REFRESH_URL) || url.includes(REFRESH_URL);

    let fetchResponse = await fetch(finalUrl, makeOptions());

    if (fetchResponse.status === 401 && !isRefreshRequest) {
      try {
        await refreshAccessToken();
        fetchResponse = await fetch(finalUrl, makeOptions());
      } catch (refreshError) {
        localStorage.removeItem(USER_LOCALSTORAGE_KEY);

        // Si no se puede refrescar el token redirigimos al login.
        window.location.href = `${STOP_APP_URL}${LOGIN_URL}`;
        return;
      }
    }

    let response = await parseResponse(fetchResponse, responseType);

    if (responseType === "json") {
      /**
       * Doble validación por legacy:
       *
       * Algunos endpoints pueden responder HTTP 200 pero incluir status 401
       * dentro del cuerpo. En ese caso se intenta refresh + retry igual.
       */

      if (String(response?.data?.status) === "401" && !isRefreshRequest) {
        try {
          await refreshAccessToken();
          fetchResponse = await fetch(finalUrl, makeOptions());
        } catch (refreshError) {
          localStorage.removeItem(USER_LOCALSTORAGE_KEY);
          // Si no se puede refrescar el token redirigimos al login.
          window.location.href = `${STOP_APP_URL}${LOGIN_URL}`;
          return;
        }
        response = await parseResponse(fetchResponse, responseType);
      }

      if (Array.isArray(response)) {
        return response;
      }

      if (response && typeof response === "object") {
        if (response.status == null) {
          response.status = fetchResponse.status;
        }

        return response;
      }

      return {
        data: response,
        status: fetchResponse.status,
      };
    }

    return response;
  } catch (error) {
    if (error.name === "AbortError") {
      return {
        ...MESSAGE_ERROR,
        message: "Petición cancelada",
        codeError: 103,
      };
    }

    return {
      ...MESSAGE_ERROR,
      message: error.message || "No se pudo realizar la petición",
    };
  } finally {
    if (finalUrl) {
      delete pendingRequests[finalUrl];
    }
  }
}